import { Component, Inject } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from "@modules/alert";
import { AiTokensApiService } from "../../services/ai-tokens-api.service";
import { AiModel } from "../../models/ai-model.model";

interface DialogData {
  mode: "create" | "edit";
  model?: AiModel;
}

@Component({
  selector: "app-ai-model-form-dialog",
  templateUrl: "./ai-model-form-dialog.component.html",
  styleUrls: ["./ai-model-form-dialog.component.scss"],
})
export class AiModelFormDialogComponent {
  form;
  submitting = false;
  mode: "create" | "edit";

  constructor(
    private fb: FormBuilder,
    private api: AiTokensApiService,
    private dialogRef: MatDialogRef<AiModelFormDialogComponent>,
    private alertService: AlertService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.mode = data.mode;
    this.form = this.fb.group({
      provider: [
        {
          value: data.model?.provider ?? "bedrock",
          disabled: this.mode === "edit",
        },
        Validators.required,
      ],
      modelId: [
        { value: data.model?.modelId ?? "", disabled: this.mode === "edit" },
        Validators.required,
      ],
      inputPricePerMillionCents: [
        data.model?.inputPricePerMillionCents ?? 0,
        [Validators.required, Validators.min(0)],
      ],
      outputPricePerMillionCents: [
        data.model?.outputPricePerMillionCents ?? 0,
        [Validators.required, Validators.min(0)],
      ],
    });
  }

  save(): void {
    if (this.form.invalid) return;
    this.submitting = true;
    const v = this.form.getRawValue();

    const handleSuccess = (messageKey: string): void => {
      this.alertService.success(this.translate.instant(messageKey));
      this.dialogRef.close(true);
    };
    const handleError = (): void => {
      this.submitting = false;
      this.alertService.error(
        this.translate.instant("AI_TOKENS.MODEL_SAVE_FAILED")
      );
    };

    if (this.mode === "create") {
      this.api.createModel(v).subscribe({
        next: () => handleSuccess("AI_TOKENS.MODEL_SAVED"),
        error: handleError,
      });
    } else {
      this.api
        .updateModel(this.data.model!.id, {
          inputPricePerMillionCents: v.inputPricePerMillionCents,
          outputPricePerMillionCents: v.outputPricePerMillionCents,
        })
        .subscribe({
          next: () => handleSuccess("AI_TOKENS.MODEL_UPDATED"),
          error: handleError,
        });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
